import { useForm, FormProvider } from 'react-hook-form';
import SubmitButton from '../core/form/SubmitButton';
import PropTypes from 'prop-types';
import { useState } from 'react';

const WizardForm = ({ defaultValues, onSubmit, isSubmitting, typeOptions, typeToUnitsMap, warnings, steps }) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const methods = useForm({
    mode: 'onChange',
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isValid },
    watch,
  } = methods;

  const outputs = watch('outputs');
  const currentStep = steps[currentStepIndex];
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;
  const isMEBalanceStep = currentStep?.id === 'meBalance';
  const hasAllocatable = outputs?.some(output => output.allocatable === true) ?? false;

  const goNext = () => {
    if (!isLastStep) setCurrentStepIndex(prev => prev + 1);
  };

  const goBack = () => {
    if (!isFirstStep) setCurrentStepIndex(prev => prev - 1);
  };

  const sharedProps = { typeOptions, typeToUnitsMap, defaultValues, warnings };

  return (
    <FormProvider {...methods}>
      {/* Only the step body scrolls; the step buttons stay pinned in a footer. */}
      <form onSubmit={handleSubmit(onSubmit)} className="flex-1 min-h-0 flex flex-col">
        <div className="flex-1 min-h-0 overflow-y-auto px-8 py-6">
          {currentStep?.Component ? (
            <currentStep.Component {...sharedProps} {...(currentStep.componentProps || {})} />
          ) : null}
        </div>

        <div className="shrink-0 flex justify-end gap-4 px-8 py-3 bg-gray-50 border-t border-gray-200">
          {!isFirstStep && (
            <button type="button" onClick={goBack} className="btn btn-outline btn-sm">
              Back
            </button>
          )}

          {!isLastStep ? (
            <button
              type="button"
              onClick={goNext}
              className="btn btn-accent btn-sm"
              disabled={!isValid || (isMEBalanceStep && !hasAllocatable)}
            >
              Continue{steps?.[currentStepIndex + 1]?.label ? ` to ${steps[currentStepIndex + 1].label}` : ''}
            </button>
          ) : (
            <SubmitButton label="Save and Create Model" isPending={isSubmitting} isDisabled={!isValid} />
          )}
        </div>
      </form>
    </FormProvider>
  );
};

WizardForm.propTypes = {
  defaultValues: PropTypes.shape({
    label: PropTypes.string,
    description: PropTypes.string,
    inputs: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        species: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        unit: PropTypes.string,
      }),
    ),
    outputs: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        species: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        unit: PropTypes.string,
        allocatable: PropTypes.bool,
        emissions: PropTypes.bool,
      }),
    ),
    simpleCosts: PropTypes.object,
    bottomUpCosts: PropTypes.object,
    costType: PropTypes.string,
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  typeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  typeToUnitsMap: PropTypes.object.isRequired,
  warnings: PropTypes.array,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string,
      Component: PropTypes.elementType.isRequired,
      componentProps: PropTypes.object,
    }),
  ).isRequired,
  initialStepId: PropTypes.string,
};

export default WizardForm;
